import {
  Box,
  CloudImage,
  Divider,
  Stack,
  mediaQuerySizesHelper,
  useIntersectionObserver,
  useTheme,
} from "@biom3/react";
import { SliceBox } from "@cms/components";
import { components } from "@cms/slices";
import { getLinkedDocument } from "@cms/utils";
import type { Content } from "@prismicio/client";
import { type SliceComponentProps, SliceZone } from "@prismicio/react";
import { motion } from "motion/react";
import { useMemo } from "react";

import {
  SliceContext,
  type SliceContextType,
  useSliceContext,
} from "@cms/context";
import { UNSELECTABLE_SX, getSafeImageOffsetSx } from "./styles";
/**
 * Props for `ParallaxSection`.
 */
export type ParallaxSectionProps = SliceComponentProps<
  Content.ParallaxSectionSlice,
  SliceContextType
>;

/**
 * Component for "ParallaxSection" Slices.
 */
const ParallaxSection = ({
  slice,
  index,
  context: sliceContext,
}: ParallaxSectionProps): JSX.Element => {
  const primaryContent = getLinkedDocument<
    Content.ParallaxSectionContentDocument,
    Content.ParallaxSectionContentDocumentData
  >(slice.primary.content);
  const { base } = useTheme();
  const isFirstSlice = index === 0;
  const { isIntersecting, domRef: setIORef } = useIntersectionObserver({
    threshold: 0.5,
  });
  const context = useSliceContext();
  const game = slice.primary.game;

  const isRightVariant = slice.variation === "rightAlign";
  let spriteOverlayPlacement = primaryContent?.data?.overlay_image_position;
  if (!spriteOverlayPlacement) {
    spriteOverlayPlacement = isRightVariant ? "top_left" : "top_right";
  }
  const initialXOffset = useMemo(() => {
    return isRightVariant ? 200 : -200;
  }, [isRightVariant]);

  return (
    <SliceBox slice={slice} index={index}>
      <Stack
        sx={{
          minh: {
            default: "600px",
            large: "700px",
          },
          pos: "relative",
          mx: "-24px",
          mt: isFirstSlice ? "base.spacing.x8" : "0px",

          ...(isRightVariant
            ? {
                pl: { default: "base.spacing.x8", large: "base.spacing.x10" },
                pr: {
                  default: "base.spacing.x8",
                  large: "290px",
                },
              }
            : {
                pl: {
                  default: "base.spacing.x8",
                  large: "290px",
                },
                pr: { default: "base.spacing.x8", large: "base.spacing.x10" },
              }),

          "&::before, &::after": {
            content: '""',
            pos: "absolute",
            h: { default: "140px", large: "220px" },
            w: "100%",
            left: "0px",
            zIndex: 1,
            ...UNSELECTABLE_SX,
          },
          "&::before": {
            background: `linear-gradient(to bottom, ${base.color.neutral[700]} 10%, transparent 100%)`,
            top: "0px",
          },
          "&::after": {
            background: `linear-gradient(to top, ${base.color.neutral[700]} 10%, transparent 100%)`,
            bottom: "0px",
          },
        }}
        justifyContent="center"
        alignItems={isRightVariant ? "flex-start" : "flex-end"}
      >
        {!isFirstSlice ? (
          <Divider
            size="xSmall"
            sx={{
              pos: "absolute",
              top: "0px",
              zIndex: 1,
              left: "40px",
              right: "40px",
              w: "calc(100% - 80px)",
            }}
          />
        ) : null}
        {slice.primary.bg_image.url ? (
          <CloudImage
            testId="backgroundImage"
            imageUrl={slice.primary.bg_image.url}
            responsiveSizes={[
              512, 640, 720, 860, 1024, 1280, 1440, 1600, 1920, 2048, 2160,
              2304, 2560, 2732, 2880, 3072, 3200, 3456, 3840, 4096,
            ]}
            // @TODO: this might need to be a bit more dynamic than this (based on responsiveness)
            relativeImageSizeInLayout="70vw"
            sx={{
              pos: "absolute",
              h: "100%",
              w: "100%",
              objectFit: "cover",
              top: "0px",
              left: "0px",
              opacity: 0.6,
              maskImage:
                "linear-gradient(to right, black 70%, transparent 100%)",
              ...UNSELECTABLE_SX,
            }}
          />
        ) : null}
        {slice.primary.overlay_image.url ? (
          <Box
            testId="heroImageOverlay"
            domRef={(el) => {
              if (!el) return;
              setIORef(el);
            }}
            sx={{
              ...UNSELECTABLE_SX,
              zIndex: 2,
              pos: "absolute",
              overflow: "hidden",
              h: "720px",
              maxw: "100%",
              aspectRatio: "1",
              top: "2%",
              d: { default: "none", large: "block" },
              ...(isRightVariant ? { right: "0px" } : { left: "0px" }),
            }}
          >
            <Box
              sx={{
                w: "100%",
                h: "100%",
                pos: "relative",
                opacity: 0,
              }}
              rc={
                <motion.div
                  animate={{
                    x: isIntersecting ? 0 : initialXOffset,
                    opacity: isIntersecting ? 1 : 0,
                  }}
                  initial={{ x: initialXOffset, opacity: 0 }}
                  transition={{
                    duration: base.motion.normal.gentle.jsDuration,
                    ease: base.motion.normal.gentle.jsEase,
                  }}
                />
              }
            >
              <CloudImage
                imageUrl={slice.primary.overlay_image.url}
                relativeImageSizeInLayout={"720px"}
                responsiveSizes={[
                  512, 640, 720, 860, 1024, 1280, 1440, 1600, 1920, 2048, 2160,
                ]}
                sx={{
                  h: "100%",
                  w: "100%",
                  objectFit: "contain",
                  top: "0px",
                  pos: "absolute",
                  transitionProperty: "left, right",
                  transitionDuration: "base.motion.normal.gentle.cssDuration",
                  transitionTimingFunction: "base.motion.normal.gentle.cssEase",
                  ...(isRightVariant
                    ? {
                        ...getSafeImageOffsetSx("right"),
                        objectPosition: "left center",
                      }
                    : {
                        ...getSafeImageOffsetSx("left"),
                        objectPosition: "right center",
                      }),
                }}
              />
            </Box>
          </Box>
        ) : null}

        <Box sx={{ pos: "relative", maxw: "350px" }} testId="sliceContainer">
          <SliceContext.Provider
            value={{
              ...context,
              data: {
                game,
              },
            }}
          >
            <SliceZone
              components={components}
              slices={primaryContent?.data?.slices}
              context={sliceContext}
            />
          </SliceContext.Provider>
          {primaryContent?.data?.overlay_image?.url ? (
            <CloudImage
              testId="contentSliceSpriteOverlay"
              imageUrl={primaryContent?.data?.overlay_image.url}
              responsiveSizes={[256, 370, 450, 512, 640, 720, 860]}
              relativeImageSizeInLayout={mediaQuerySizesHelper([
                { rule: "default", width: "160px" },
                { rule: "minWidth", breakpoint: "large", width: "190px" },
              ])}
              sx={{
                pos: "absolute",
                zIndex: 3,
                w: { default: "160px", large: "190px" },
                top: { default: "-120px", large: "-140px" },
                ...UNSELECTABLE_SX,

                ...(spriteOverlayPlacement === "top_left"
                  ? {
                      left: "base.spacing.x5",
                    }
                  : {
                      right: "base.spacing.x5",
                    }),
              }}
            />
          ) : null}
        </Box>
      </Stack>
    </SliceBox>
  );
};

export default ParallaxSection;
